/**
 * Telefone brasileiro: máscara de digitação e validação.
 *
 * A validação existe para barrar telefone de fachada — dígito repetido
 * ("99999-9999") ou sequência ("98765-4321") — que passa em qualquer regex de
 * formato mas nunca vai receber uma ligação. A mesma regra vive no backend
 * (`backend/utils/telefone.py`); aqui ela é só o retorno imediato para quem digita.
 */

const AREA_CODE_LENGTH = 2;
const LANDLINE_SUBSCRIBER_LENGTH = 8;
const MOBILE_SUBSCRIBER_LENGTH = 9;
const MOBILE_PREFIX = '9';
const LANDLINE_FIRST_DIGITS = new Set(['2', '3', '4', '5']);
const COUNTRY_CODE = '55';

export const PHONE_MAX_DIGITS = AREA_CODE_LENGTH + MOBILE_SUBSCRIBER_LENGTH;
/** "(11) 98472-6153" */
export const PHONE_MASK_MAX_LENGTH = 15;
/**
 * Teto do atributo `maxLength` do input, folgado em relação à máscara: o
 * preenchimento automático do navegador entrega "+55 (11) 98472-6153" (19
 * caracteres) e o corte do próprio input aconteceria antes da normalização.
 */
export const PHONE_INPUT_MAX_LENGTH = 20;

/** DDDs em uso no Brasil (Plano Nacional de Numeração da Anatel). */
const VALID_AREA_CODES = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

export const PHONE_ERROR_REQUIRED = 'Informe seu telefone';
export const PHONE_ERROR_INCOMPLETE =
  'Telefone incompleto. Informe o DDD e o número completo';
export const PHONE_ERROR_AREA_CODE = 'DDD inexistente. Confira os dois primeiros dígitos';
export const PHONE_ERROR_MOBILE_PREFIX = 'Celular deve começar com 9 depois do DDD';
export const PHONE_ERROR_LANDLINE_PREFIX = 'Telefone fixo deve começar com 2, 3, 4 ou 5 depois do DDD';
export const PHONE_ERROR_TOO_LONG =
  'Telefone com dígitos a mais. Confira o número';
export const PHONE_ERROR_FAKE =
  'Telefone inválido. Não use dígitos repetidos nem números em sequência';

const onlyDigits = (value?: string | null): string => String(value ?? '').replace(/\D/g, '');

const INTERNATIONAL_DIGITS =
  COUNTRY_CODE.length + AREA_CODE_LENGTH + MOBILE_SUBSCRIBER_LENGTH;

/**
 * Descarta o "+55" que o preenchimento automático do navegador costuma trazer.
 *
 * Exige marca de número internacional — o "+" escrito, ou os 13 dígitos que só
 * o formato E.164 alcança. Sem isso, quem é do DDD 55 (Santa Maria/RS) e passa
 * um dígito do número perderia o próprio DDD a cada tecla: 12 dígitos aqui são
 * excesso de digitação, não código de país.
 */
/**
 * Doze dígitos começando por "55" sem o "+" são ambíguos por natureza: tanto
 * "+55 11 3326-8471" quanto o assinante do DDD 55 que digitou um dígito a mais
 * chegam aqui iguais. Escolher "a leitura que der um telefone válido" parece
 * resolver, mas aceita número errado calado — "553326847112" (dois dígitos a
 * mais em 55 3326-8471) viraria o (33) 2684-7112, de outra pessoa. Por isso a
 * ambiguidade é recusada, não adivinhada: quem cola o formato internacional
 * traz o "+" junto, e aí não há dúvida.
 */
/** Comprimento que só o formato internacional alcança (país + DDD + número). */
const isInternationalLength = (digits: string): boolean =>
  digits.startsWith(COUNTRY_CODE) &&
  (digits.length === COUNTRY_CODE.length + AREA_CODE_LENGTH + LANDLINE_SUBSCRIBER_LENGTH ||
    digits.length === INTERNATIONAL_DIGITS);

/**
 * Quem escreveu "+" declarou o código do país, e vale em qualquer tamanho: sem
 * isso um número internacional pela metade cai nos 11 dígitos e é lido como DDD
 * 55, acusando o problema errado em vez de "incompleto". Sem o "+", só os 13
 * dígitos do E.164 contam — 12 seria dígito digitado a mais de quem é do DDD 55.
 */
const withoutCountryCode = (digits: string, hasPlusSign: boolean): string => {
  if (!digits.startsWith(COUNTRY_CODE)) return digits;

  const marcado = hasPlusSign || digits.length === INTERNATIONAL_DIGITS;

  return marcado ? digits.slice(COUNTRY_CODE.length) : digits;
};

/**
 * Só os dígitos do número nacional — é o formato enviado à API.
 *
 * Não corta o que sobra: dígito a mais tem de chegar à validação e ser
 * recusado, como o backend faz. Cortar aqui devolveria, calado, o telefone de
 * outra pessoa. Quem limita o tamanho para caber na tela é a máscara.
 */
export const normalizePhoneDigits = (value?: string | null): string => {
  const bruto = String(value ?? '');
  return withoutCountryCode(onlyDigits(bruto), bruto.trimStart().startsWith('+'));
};

const hasSingleRepeatedDigit = (digits: string): boolean =>
  digits.length > 0 && new Set(digits).size === 1;

// Passo em aritmética módulo 10, para que "7890" continue sendo sequência
// depois da virada do 9 para o 0.
const stepBetween = (from: string, to: string): number => (Number(to) - Number(from) + 10) % 10;

const ASCENDING_STEP = 1;
const DESCENDING_STEP = 9;

const isRunOfConsecutiveDigits = (digits: string): boolean => {
  if (digits.length < 2) return false;

  const step = stepBetween(digits[0], digits[1]);
  if (step !== ASCENDING_STEP && step !== DESCENDING_STEP) return false;

  return digits
    .split('')
    .every((digit, index) => index === 0 || stepBetween(digits[index - 1], digit) === step);
};

const looksFabricated = (subscriber: string): boolean => {
  const afterMobilePrefix =
    subscriber.length === MOBILE_SUBSCRIBER_LENGTH ? subscriber.slice(1) : '';

  // Dígito repetido vale também para o trecho depois do 9 obrigatório do
  // celular: "98888-8888" não é telefone de ninguém.
  const repetido =
    hasSingleRepeatedDigit(subscriber) || hasSingleRepeatedDigit(afterMobilePrefix);

  // Sequência, ao contrário, só conta quando cobre o número inteiro. Cobrar
  // também dos 8 dígitos seguintes recusava telefone real — (11) 99876-5432
  // existe, e o assinante ficava sem conseguir se cadastrar.
  return repetido || isRunOfConsecutiveDigits(subscriber);
};

const maskNationalNumber = (digits: string): string => {
  if (digits.length <= AREA_CODE_LENGTH) return digits;

  const areaCode = digits.slice(0, AREA_CODE_LENGTH);
  const subscriber = digits.slice(AREA_CODE_LENGTH);
  if (subscriber.length <= 4) return `(${areaCode}) ${subscriber}`;

  const splitAt = subscriber.length > LANDLINE_SUBSCRIBER_LENGTH ? 5 : 4;
  return `(${areaCode}) ${subscriber.slice(0, splitAt)}-${subscriber.slice(splitAt)}`;
};

/**
 * Aplica "(11) 98472-6153" progressivamente, tolerando entrada já formatada.
 *
 * Enquanto o código do país está sendo digitado e o número ainda não fechou, o
 * "+55 " permanece à vista: é o "+" que distingue número internacional de
 * dígito a mais, e apagá-lo do campo faria a tecla seguinte cair no caminho
 * errado — o número seria trocado por outro, sem aviso. Assim que o número
 * internacional se completa, ele colapsa para a forma nacional.
 */
export const applyPhoneMask = (value?: string | null): string => {
  const bruto = String(value ?? '');
  const digits = onlyDigits(bruto);

  if (bruto.trimStart().startsWith('+') && !isInternationalLength(digits)) {
    if (!digits.startsWith(COUNTRY_CODE.slice(0, digits.length))) {
      return maskNationalNumber(digits.slice(0, PHONE_MAX_DIGITS));
    }
    if (digits.length < COUNTRY_CODE.length) return `+${digits}`;

    const nacional = digits.slice(COUNTRY_CODE.length, COUNTRY_CODE.length + PHONE_MAX_DIGITS);
    if (!nacional) return `+${COUNTRY_CODE}`;

    return `+${COUNTRY_CODE} ${maskNationalNumber(nacional)}`;
  }

  const nacional = normalizePhoneDigits(bruto);
  // O que passa do tamanho fica à vista, sem formatação: cortar aqui esconderia
  // o dígito sobrando e mandaria adiante um telefone válido que não é o que a
  // pessoa digitou.
  return (
    maskNationalNumber(nacional.slice(0, PHONE_MAX_DIGITS)) + nacional.slice(PHONE_MAX_DIGITS)
  );
};

/** Mensagem do primeiro problema encontrado, ou null quando o telefone serve. */
export const getPhoneError = (value?: string | null): string | null => {
  const digits = normalizePhoneDigits(value);
  if (digits.length === 0) return PHONE_ERROR_REQUIRED;
  if (digits.length > PHONE_MAX_DIGITS) return PHONE_ERROR_TOO_LONG;

  if (
    digits.length !== AREA_CODE_LENGTH + LANDLINE_SUBSCRIBER_LENGTH &&
    digits.length !== AREA_CODE_LENGTH + MOBILE_SUBSCRIBER_LENGTH
  ) {
    return PHONE_ERROR_INCOMPLETE;
  }

  if (!VALID_AREA_CODES.has(Number(digits.slice(0, AREA_CODE_LENGTH)))) {
    return PHONE_ERROR_AREA_CODE;
  }

  const subscriber = digits.slice(AREA_CODE_LENGTH);
  if (subscriber.length === MOBILE_SUBSCRIBER_LENGTH) {
    if (!subscriber.startsWith(MOBILE_PREFIX)) return PHONE_ERROR_MOBILE_PREFIX;
  } else if (!LANDLINE_FIRST_DIGITS.has(subscriber[0])) {
    return PHONE_ERROR_LANDLINE_PREFIX;
  }

  return looksFabricated(subscriber) ? PHONE_ERROR_FAKE : null;
};

export const isValidBrazilianPhone = (value?: string | null): boolean =>
  getPhoneError(value) === null;
